/**
 * Expression sites: where an expression lives in a document and which type it must have (plan §7.3,
 * §4.4). Split from `binding.ts` so blocks and code elements can declare their sites without the
 * DataBinding schemas.
 *
 * Owner: doc-engine (wave 1), expr-language reads it.
 */
import { z } from 'zod';
import { ApiKey } from '../ids';

/**
 * Structural mirror of `ExprType` from `@dataroom/expr` (core cannot depend on expr). The expected
 * type of an expression site; expr-language checks it with `assertExpression`.
 */
export type ExprTypeRef =
  | { kind: 'text' | 'number' | 'percent' | 'boolean' | 'date' | 'datetime' | 'duration' | 'any' | 'null' }
  | { kind: 'money'; currency?: string }
  | { kind: 'list'; of: ExprTypeRef }
  | { kind: 'record'; objectKey: string };
export const ExprTypeRef: z.ZodType<ExprTypeRef> = z.lazy(() =>
  z.union([
    z
      .object({
        kind: z.enum(['text', 'number', 'percent', 'boolean', 'date', 'datetime', 'duration', 'any', 'null']),
      })
      .strict(),
    z
      .object({
        kind: z.literal('money'),
        currency: z
          .string()
          .regex(/^[A-Z]{3}$/)
          .optional(),
      })
      .strict(),
    z.object({ kind: z.literal('list'), of: ExprTypeRef }).strict(),
    z.object({ kind: z.literal('record'), objectKey: ApiKey }).strict(),
  ]),
);
export const T_BOOLEAN: ExprTypeRef = { kind: 'boolean' };
export const T_TEXT: ExprTypeRef = { kind: 'text' };
export const T_NUMBER: ExprTypeRef = { kind: 'number' };

/** An expression in the canonical language of `@dataroom/expr` (checked by expr-language). */
export const ExprSource = z.string().trim().min(1).max(4000);

/**
 * Where an expression lives in a document, so expr-language can typecheck every site, build the
 * dependency index and rename fields. `path` is relative to the artefact (or the node's attrs).
 */
export type ExprSite = {
  path: (string | number)[];
  source: string;
  /** `expression` = one expression, `template` = text with `{{ … }}` tokens, `condition` = boolean. */
  role: 'expression' | 'template' | 'condition';
  /**
   * Names in scope: `record` = the bound record, `row` = one row of a table or repeater (plus
   * `record`), `document` = record plus page tokens (`page`, `pages`, `section`).
   */
  context: 'record' | 'row' | 'document';
  expected?: ExprTypeRef;
};
